import {
  HeaderSwitcher,
  KdsButton,
  KdsLink,
} from "@kazaar-platform/design-system";
import { RiExternalLinkLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import clsx from "clsx";
import { useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FeatureFlagUpsertCommandFeatureEnum } from "@/config/apiClient";
import { isMetadataWithUrl } from "@/helpers/validators/isMetadataWithUrl";
import { useGetIsFeatureActive } from "@/hooks";
import { useHeaderTree } from "@/hooks/useHeaderTree";
import { currentWorkspaceAtom } from "@/store";
import { ConnectedBreadcrumb } from "../Breadcrumb";
import { HeaderPopover } from "./_partials/HeaderPopover/HeaderPopover";
import { type HeaderProps } from "./Header.types";

export const Header = ({ avatarUrl, logo, classnames }: HeaderProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const currentWorkspace = useAtomValue(currentWorkspaceAtom);
  const { headerTree, selectedWorkspaceValue, onWorkspaceSelect } =
    useHeaderTree();

  const [isHeaderSwitcherOpen, setIsHeaderSwitcherOpen] = useState(false);

  const { isActive: isReportingFeatureActive, metadata } =
    useGetIsFeatureActive(FeatureFlagUpsertCommandFeatureEnum.Reporting);

  const handleSelectedWorkspace = (value: number) => {
    void navigate("/");
    // Delay workaround to avoid reload all queries subscribed to workspaceId state
    setTimeout(() => {
      onWorkspaceSelect(value);
    }, 250);
  };

  const handleReportingOnPress = useCallback(() => {
    if (!isMetadataWithUrl(metadata)) {
      return;
    }

    window.open(metadata.url, "_blank");
  }, [metadata]);

  return (
    <div
      data-testid="ok"
      className={clsx(
        "relative h-[100px] w-screen border-b border-slate-200 sm:!h-[60px]",
        classnames
      )}
    >
      <header className="flex h-full w-full flex-wrap items-center justify-between overflow-hidden pb-1 pr-6 sm:!flex-nowrap sm:!pb-0">
        <div className="flex w-full items-center justify-center sm:!w-fit">
          <div className="w-[236px] border-r border-r-outlined-border">
            <HeaderSwitcher
              isOpen={isHeaderSwitcherOpen}
              setIsOpen={setIsHeaderSwitcherOpen}
              logo={logo}
              variant="light"
              tree={headerTree ?? []}
              name={currentWorkspace?.name}
              selected={selectedWorkspaceValue ?? 0}
              onChange={handleSelectedWorkspace}
            />
          </div>

          <div className="pl-sp-md">
            <ConnectedBreadcrumb />
          </div>
        </div>

        <div className="flex w-full items-center justify-between gap-sp-xs sm:!w-fit sm:!justify-end md:!gap-sp-md">
          <div className="jimo-widget">
            <KdsLink state="neutral">{t("header.whatsNew")}</KdsLink>
          </div>

          {isReportingFeatureActive && (
            <KdsButton
              variant="outlined"
              iconRight={RiExternalLinkLine}
              color="neutral"
              onPress={handleReportingOnPress}
            >
              {t("header.reportingButtonLabel")}
            </KdsButton>
          )}

          <HeaderPopover avatarUrl={avatarUrl} />
        </div>
      </header>
    </div>
  );
};
